// לקוח ל-endpoints של "מלא": שיחות (10), פאנלים, קונסנט (08), אישור הצעות (05), סודות צימוד (02), ממשקי יצירה (09).
// כל קריאה עוברת ב-authed של api.ts (token, רענון, 401 → login, שגיאה = textHe מהשרת).
import type { z } from 'zod';
import { ApiError, authed, json } from './api.ts';
import { AuthExpired, getAccessToken } from './auth.ts';
import { T } from './strings.ts';
import {
  ArtifactInterfaces,
  AttachSecretsResponse,
  ConsentDecideResponse,
  ConsentDetail,
  ConsentList,
  ConversationList,
  CreateConversationResponse,
  CreateTurnResponse,
  DecideProposalResponse,
  DecideRequestResponse,
  HistoryResponse,
  LoginLinkResponse,
  PanelAi1Full,
  PanelAi2Full,
  UploadFileResponse,
  VoiceInfo,
  VoiceTranscript,
} from './aiContract.ts';

const enc = encodeURIComponent;
const send = (method: string, body: unknown): RequestInit => ({ method, headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });

export type ConnSecretInput = { host: string; port?: number; db?: string; service?: string; user: string; password: string };

export const aiApi = {
  /** 26: קישור כניסה חד-פעמי למשתמש (admin) — {url, expiresAt}; מוצג פעם אחת. */
  loginLink: (userId: string) => json(LoginLinkResponse, `/api/users/${enc(userId)}/login-link`, send('POST', {})),
  listConversations: () => json(ConversationList, '/api/conversations'),
  /** web-conversation-list-search: כותרת + ההודעות של המשתמש עצמו (2–100 תווים) */
  searchConversations: (q: string, level: 1 | 2) => json(ConversationList, `/api/conversations?level=${level}&q=${enc(q)}`),
  createConversation: (level: 1 | 2) => json(CreateConversationResponse, '/api/conversations', send('POST', { level })),
  history: (id: string) => json(HistoryResponse, `/api/conversations/${enc(id)}/history`),
  /** טקסט ו/או קבצים שהועלו (uploadFile → id); בזמן תור חי — נכנסת אליו (sayId). */
  /** clientId — מזהה השליחה (uuid): ניסיון חוזר אחרי כשל רשת/deploy מחזיר את מה שכבר נקלט, בלי כפילות. */
  sendTurn: (id: string, text: string, fileIds: string[] = [], clientId?: string) =>
    json(CreateTurnResponse, `/api/conversations/${enc(id)}/turns`, send('POST', { text, ...(fileIds.length ? { fileIds } : {}), ...(clientId ? { clientId } : {}) })),
  /** העלאת קובץ אחד (multipart) עם התקדמות — XHR, כי fetch לא מדווח על העלאה. */
  uploadFile: async (id: string, file: File, onProgress?: (fraction: number) => void, signal?: AbortSignal) => {
    let token: string;
    try {
      token = await getAccessToken();
    } catch (e) {
      if (e instanceof AuthExpired) throw new ApiError(401, 'unauthenticated', T.sessionExpired);
      throw e;
    }
    const form = new FormData();
    form.append('file', file, file.name);
    return new Promise<z.infer<typeof UploadFileResponse>['file']>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `/api/conversations/${enc(id)}/files`);
      xhr.setRequestHeader('authorization', `Bearer ${token}`);
      xhr.responseType = 'text';
      xhr.upload.onprogress = (e) => onProgress?.(e.lengthComputable ? e.loaded / e.total : 0);
      xhr.onerror = () => reject(new ApiError(0, 'network', T.network));
      xhr.onabort = () => reject(new ApiError(0, 'aborted', T.ai.attach.canceled));
      xhr.onload = () => {
        let body: unknown = null;
        try {
          body = JSON.parse(xhr.responseText || 'null');
        } catch {}
        if (xhr.status >= 200 && xhr.status < 300) {
          const p = UploadFileResponse.safeParse(body);
          return p.success ? resolve(p.data.file) : reject(new ApiError(xhr.status, 'bad_response', T.badResponse));
        }
        const err = (body as { error?: { code?: string; textHe?: string } } | null)?.error;
        reject(new ApiError(xhr.status, err?.code ?? 'server', err?.textHe ?? (xhr.status === 413 ? T.ai.attach.tooLarge : T.serverError(xhr.status))));
      };
      signal?.addEventListener('abort', () => xhr.abort(), { once: true });
      xhr.send(form);
    });
  },
  /** הקובץ עצמו (בעלים בלבד) — blob לתמונה ממוזערת/להורדה; ה-token בכותרת, לא ב-URL. */
  fetchFile: async (id: string, fileId: string) => (await authed(`/api/conversations/${enc(id)}/files/${enc(fileId)}`)).blob(),
  /** conversation-voice-input: האם התמלול מוגדר במופע + המגבלות. */
  voiceInfo: () => json(VoiceInfo, '/api/voice'),
  /** השמע עצמו כגוף (content-type = סוג ההקלטה) → טקסט; לא נשמר בשרת. */
  transcribe: async (audio: Blob) => (await json(VoiceTranscript, '/api/voice/transcribe', { method: 'POST', headers: { 'content-type': audio.type || 'audio/webm' }, body: audio })).text,
  cancelTurn: (turnId: string) => authed(`/api/turns/${enc(turnId)}/cancel`, send('POST', {})).then(() => undefined),
  archive: (id: string) => authed(`/api/conversations/${enc(id)}/archive`, send('POST', {})).then(() => undefined),
  panelAi1: () => json(PanelAi1Full, '/api/panel/ai1'),
  /** conversationId — "מה בנינו בשיחה הזו" / "ממתין לך" / התצוגה המקדימה של השיחה; בלי — כללי למשתמש. */
  panelAi2: (conversationId?: string) => json(PanelAi2Full, `/api/panel/ai2${conversationId ? `?conversation=${enc(conversationId)}` : ''}`),
  decideProposal: (id: string, decision: 'approve' | 'reject', noteHe = '') =>
    json(DecideProposalResponse, `/api/tree/proposals/${enc(id)}/decide`, send('POST', { decision, noteHe })),
  /** conversation-admin-requests: "טפל בזה" / "דחה" על בקשת משתמש — בזהות האדם; approve מחזיר את שיחת מנהל הבינה שנפתחה. */
  decideRequest: (id: string, decision: 'approve' | 'decline', noteHe = '') =>
    json(DecideRequestResponse, `/api/admin-requests/${enc(id)}/decide`, send('POST', noteHe ? { decision, noteHe } : { decision })),
  /** הסיסמה עוברת רק בגוף הבקשה הזו — לא בשיחה, לא ב-storage. */
  attachPairingSecrets: (pairingId: string, bundle: Record<string, ConnSecretInput>) =>
    json(AttachSecretsResponse, `/api/connector/pairings/id/${enc(pairingId)}/secrets`, send('POST', bundle)),
  listConsents: () => json(ConsentList, '/api/consents?status=pending'),
  getConsent: (id: string, from: number, limit: number) => json(ConsentDetail, `/api/consents/${enc(id)}?from=${from}&limit=${limit}`),
  decideConsent: (id: string, approve: 'all' | string[], reject?: string[]) =>
    json(ConsentDecideResponse, `/api/consents/${enc(id)}/decide`, send('POST', reject ? { approve, reject } : { approve })),
  artifactInterfaces: (slug: string) => json(ArtifactInterfaces, `/api/artifacts/${enc(slug)}/interfaces`),
  setArtifactInterface: (slug: string, interfaceId: string, mode: 'auto' | 'consent') =>
    authed(`/api/artifacts/${enc(slug)}/interfaces`, send('PUT', { interfaceId, mode })).then(() => undefined),
};
